import { httpsCallable } from "firebase/functions";
import type { LeaderboardBoard } from "@/rank/leaderboard-types";
import type { DungeonShopConfig } from "@/dungeon/dungeon-shop-types";
import type { RankBadgeConfigBoard, UserWeeklyBadges } from "@/rank/leaderboard-types";
import { getCloudFunctions } from "@/cloud/firebase-app";

function fn() {
  const f = getCloudFunctions();
  if (!f) throw new Error("Firebase ยังไม่ตั้งค่า");
  return f;
}

export type RaidPointsPayload = {
  raidPoints: number;
  raidPointsMax: number;
  msUntilNextPoint: number;
  lastRegenAt: number;
};

export type LeaderboardRow = {
  rank: number;
  uid: string;
  displayName: string;
  value: number;
};

export type StartRaidResponse = {
  runId: string;
  defenderUid: string;
  defenderName: string;
  defenderRaidRankPoints: number;
  maxFloors: number;
  floors: unknown[];
  homeRoom: unknown;
  defenderEquippedShadow: unknown | null;
  raidPoints: RaidPointsPayload;
};

export async function callSyncPublicStats(input: {
  displayName: string;
  towerHighestCleared: number;
  wealthCoins: number;
}): Promise<void> {
  await httpsCallable(fn(), "syncPublicStats")(input);
}

export async function callSyncEquippedShadow(input: {
  equipped: {
    name: string;
    spritesheetDataUrl: string;
    grid: unknown;
    actions: Record<string, string>;
    statAllocation: Record<string, number>;
  } | null;
}): Promise<void> {
  await httpsCallable(fn(), "syncEquippedShadow")(input);
}

export async function callGetRaidStatus(): Promise<RaidPointsPayload> {
  const res = await httpsCallable(fn(), "getRaidStatus")({});
  return res.data as RaidPointsPayload;
}

export async function callPublishDungeon(payload: {
  published: boolean;
  maxFloors: number;
  floors: unknown[];
  homeRoom: unknown;
  displayName: string;
}): Promise<void> {
  await httpsCallable(fn(), "publishDungeon")(payload);
}

export async function callStartRaid(): Promise<StartRaidResponse> {
  const res = await httpsCallable(fn(), "startRaid")({});
  return res.data as StartRaidResponse;
}

export async function callSurrenderRaid(runId: string): Promise<void> {
  await httpsCallable(fn(), "surrenderRaid")({ runId });
}

export async function callCompleteRaid(runId: string): Promise<{ pointsGain: number }> {
  const res = await httpsCallable(fn(), "completeRaid")({ runId });
  return res.data as { pointsGain: number };
}

export type HomeStructureDefenseResponse = {
  damageTaken: number;
  lastDamageAt: number;
  brokenUntil: number;
  maxHp: number;
  currentHp: number;
  defense: number;
  regenPerSec: number;
  potionsLeft?: number;
  destroyed?: boolean;
  damageApplied?: number;
};

export async function callSyncHomeStructureDefense(input: {
  defenderUid?: string;
}): Promise<HomeStructureDefenseResponse> {
  const res = await httpsCallable(fn(), "syncHomeStructureDefense")(input);
  return res.data as HomeStructureDefenseResponse;
}

export async function callCommitRaidHomeStructureDamage(input: {
  runId: string;
  defenderUid: string;
  damageThisRun: number;
}): Promise<HomeStructureDefenseResponse> {
  const res = await httpsCallable(fn(), "commitRaidHomeStructureDamage")(input);
  return res.data as HomeStructureDefenseResponse;
}

export async function callUseHomeStructurePotion(input: {
  defenderUid?: string;
}): Promise<HomeStructureDefenseResponse & { potionsLeft: number }> {
  const res = await httpsCallable(fn(), "useHomeStructurePotion")(input);
  return res.data as HomeStructureDefenseResponse & { potionsLeft: number };
}

export async function callGetLeaderboard(board: LeaderboardBoard): Promise<{
  board: LeaderboardBoard;
  rows: LeaderboardRow[];
  me?: LeaderboardRow;
}> {
  const res = await httpsCallable(fn(), "getLeaderboard")({ board });
  return res.data as {
    board: LeaderboardBoard;
    rows: LeaderboardRow[];
    me?: LeaderboardRow;
  };
}

export async function callGetRankBadgeConfig(): Promise<{ boards: RankBadgeConfigBoard[] }> {
  const res = await httpsCallable(fn(), "getRankBadgeConfig")({});
  return res.data as { boards: RankBadgeConfigBoard[] };
}

export async function callSetRankBadgeConfig(boards: RankBadgeConfigBoard[]): Promise<void> {
  await httpsCallable(fn(), "setRankBadgeConfig")({ boards });
}

export async function callGetMyWeeklyBadges(): Promise<{ weekly: UserWeeklyBadges | null }> {
  const res = await httpsCallable(fn(), "getMyWeeklyBadges")({});
  return res.data as { weekly: UserWeeklyBadges | null };
}

export async function callRunWeeklyLeaderboardNow(): Promise<{ weekKey: string }> {
  const res = await httpsCallable(fn(), "runWeeklyLeaderboardNow")({});
  return res.data as { weekKey: string };
}

export async function callGmGrantLeaderboardStat(input: {
  board: LeaderboardBoard;
  amount: number;
  targetUid?: string;
}): Promise<{ board: LeaderboardBoard; value: number }> {
  const res = await httpsCallable(fn(), "gmGrantLeaderboardStat")(input);
  return res.data as { board: LeaderboardBoard; value: number };
}

export async function callGetDungeonShopConfig(): Promise<{ config: Partial<DungeonShopConfig> }> {
  const res = await httpsCallable(fn(), "getDungeonShopConfig")({});
  return res.data as { config: Partial<DungeonShopConfig> };
}

export async function callSetDungeonShopConfig(config: DungeonShopConfig): Promise<void> {
  await httpsCallable(fn(), "setDungeonShopConfig")({ config });
}

export type GmPlayerRow = {
  uid: string;
  email: string | null;
  displayName: string;
  disabled: boolean;
  createdAt: number | null;
  lastSignInAt: number | null;
  towerHighestCleared: number;
  wealthCoins: number;
  raidRankPoints: number;
  dungeonPublished: boolean;
};

export async function callGmListPlayers(input?: {
  pageToken?: string | null;
  limit?: number;
}): Promise<{ players: GmPlayerRow[]; nextPageToken: string | null }> {
  const res = await httpsCallable(fn(), "gmListPlayers")(input ?? {});
  return res.data as { players: GmPlayerRow[]; nextPageToken: string | null };
}

export async function callGmResetPlayer(uid: string): Promise<void> {
  await httpsCallable(fn(), "gmResetPlayer")({ uid });
}

export async function callGmSetPlayerBanned(uid: string, banned: boolean): Promise<void> {
  await httpsCallable(fn(), "gmSetPlayerBanned")({ uid, banned });
}
